import { CONTACT_TYPE, OrderTemplate, Worker } from "@/types";
import { ConfirmModal, ContactModal, FindPersonModal, RemoveModal } from "#/organisms";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useContacts,
  useFields,
  useModal,
  useNotes,
  useOrderTemplate,
} from "@/hooks";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { Alert } from "@/components/atoms";
import { Booking } from "#/pages";
import { CardHandle } from "@/components/pages/Order/Cards/Cards.interface";
import { ChangePackageModal } from "@/components/organisms/Modais/ChangePackage";
import { CreateOrderParam } from "@/api/order/interface";
import { ORDER_STEP } from "#/pages/Order/Order/Order.interface";
import { OrderCreate } from "#/pages";
import { extraText } from "@/helpers";
import { useApi } from "@/api";

const OrderNewView = () => {
  const { brid } = useParams<'brid'>();
  const { pkgid } = useParams<'pkgid'>();
  const { baseorderid } = useParams<'baseorderid'>();

  const bookingRef = useRef<CardHandle>(null);

  const location = useLocation();
  const navigate = useNavigate();
  const { customer, order: orderEntity, orderTemplate: orderTemplateApi, session } = useApi();
  const {
    showModal: showConfirmModal,
    onCloseModal: onCloseConfirmModal,
    onOpenModal: onOpenConfirmModal
  } = useModal();

  const isAdmin = useMemo(() => session.user?.admin || false, [session.user]);

  const [selectedPreferredWorker, setSelectedPreferredWorker] = useState<Worker | null>(null);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [loadingOrderTemplates, setLoadingOrderTemplates] = useState(false);
  const contactsProps = useContacts();
  const { contacts, onAddCustomerContact } = contactsProps;
  const { notes, onAddNote, setNotes } = useNotes();
  const [orderId, setOrderId] = useState('');
  const {showModal, onCloseModal, onOpenModal } = useModal();

  const {
    showModal: showChangePackageModal,
    onCloseModal: onCloseChangePackageModal,
    onOpenModal: onOpenChangePackageModal 
  } = useModal();

  const handleChangeWorker = useCallback(() => {
    onOpenModal();
  }, []);

  const {
    getOrderTemplateDetails,
    selectedOrderTemplate,
    setSelectedOrderTemplate
  } = useOrderTemplate();
  const {
    ...fields
  } = useFields();

  const fetchOrderTemplateDetails = async (orderTypeId: number) => {
    await getOrderTemplateDetails(orderTypeId);
  };

  const handleFindPerson = async (search: string) => {
    const res = await orderEntity.findWorker(search);
      setWorkers(res);
  };

  const handleSelect = (id: string) => {

    const selected = workers.find((item) => item.id === id);

      if (selected) {
        setSelectedPreferredWorker(selected);
      }
  };

  useEffect(() => {
    if (pkgid) {
        fetchOrderTemplateDetails(Number(pkgid));
        contactsProps.setContacts([]);
        orderEntity.clearOrderDetails();
        fields.appointmentDuration.setValue('60');
    }
    }, []);

  const getOrderData = (): CreateOrderParam => {
    const {
      addressString,
      allowFreelance,
      appointmentDate,
      appointmentDuration,
      appointmentTime,
      bedrooms,
      contactVendorDirectly,
      doorNumber,
      flat,
      floorArea,
      imageMotionRequested,
      keyNotes,
      keys,
      keysTime,
      keysTimeOption,
      keysWith,
      keysWithDetail,
      notifyViaSMS,
      orderOfferPrice,
      orderReference,
      parking,
      photoCount,
      poNumber,
      preferredWorker,
      sendNotifications,
      videoMotionRequested,
      virtualStagingRequested,
      virtualTourPartial
    } = fields;
    const dateTime = appointmentDate.value;
    const time = appointmentTime.value ? appointmentTime.value.split(':') : [];

    if (time.length > 0 && dateTime) {
      dateTime.setHours(Number(time[0]));
      dateTime.setMinutes(Number(time[1]));
    }

    dateTime?.setMinutes(dateTime.getMinutes() - dateTime.getTimezoneOffset());

    const selectedPhotos = JSON.parse(sessionStorage.getItem('vsPhotos') || "[]");

    return {
      baseOrderId: baseorderid,
      bedrooms: Number(bedrooms.value),
      common: {
        appointmentDateTime: !contactVendorDirectly.checked ? dateTime?.toISOString() || null : null,
        appointmentDuration: Number(appointmentDuration.value) || 0,
        contactVendorDirectly: contactVendorDirectly.checked,
        notifyViaSMS: notifyViaSMS.checked,
        sendNotifications: sendNotifications.checked,
      },
      contacts: contacts.map((item) => ({
        contactId: null,
        customerId: '',
        email: item.email,
        name: item.name,
        phone1: item.phone1,
        phone2: item.phone2,
        sendInvoice: item.sendInvoice,
        type: CONTACT_TYPE.AGENT,
      })),
      customerId: brid || '',
      description: selectedOrderTemplate?.isVirtual ? orderReference.value  : selectedOrderTemplate?.description,
      floorArea: Number(floorArea.value),
      imageMotionRequested: imageMotionRequested.checked,
      isVirtual: selectedOrderTemplate?.isVirtual || false,
      keys: keys.checked,
      keysTime: keysTime.value || '',
      keysTimeOption: keysTimeOption.value,
      keysWith: keysWith.value,
      keysWithDetail: keysWithDetail.value,
      notes: keyNotes.value,
      orderNotes: notes.map((item) => ({ content: item.content })),
      orderOfferPrice: Number(orderOfferPrice.value),
      orderReference: orderReference.value,
      parking: parking.value,
      photoCount: Number(photoCount.value),
      poNumber: poNumber.value,
      preferredWorker: preferredWorker.value,
      propertyAddress: {
        addressString: addressString.value,
        doorNumber: doorNumber.value,
        flat: flat.value,
      },
      selectedPhotos: selectedPhotos,
      useOrderOffer: allowFreelance.checked,
      videoMotionRequested: videoMotionRequested.checked,
      virtualStagingRequested: videoMotionRequested.checked,
      virtualTourPartial: virtualTourPartial.checked,
    }
  }

  const placeOrder = async (templateId: number) => {
    const validationResult = bookingRef.current?.validate();

    if (!(validationResult?.isValid ?? true)) {
      
      const err = validationResult?.validationErrors || [];

      if (err.length > 0)
      {
        setErrors([err[0]]);
        setTimeout(() => {
          setErrors([])
        }, 3500);
      }

      return;
    }

    const orderData = getOrderData();

    orderEntity.createOrder(templateId.toString(), orderData).then(( { orderId: id }) => {
        setOrderId(id || '');
        
        if (id) {
          onOpenConfirmModal();
        }
    });
  }

  const handleConfirmClosed = () => {
    onCloseConfirmModal();
    
    if (orderId) {
      navigate( `/order-view${isAdmin ? '-backend' : ''}/order=${orderId}`);
    }
    else {
      navigate(isAdmin ? '/orders-unmaintained' :  '/customer-orders');
    }
  }

  const handleSaveOrder = async () => {
    await placeOrder(selectedOrderTemplate?.id ?? 0);
  };

  const handleChangePackage = async() => {
    setLoadingOrderTemplates(true);
    onOpenChangePackageModal();
    const branches = await customer.getBranches(session.user?.id || '');
    orderTemplateApi.getList(branches);
    setLoadingOrderTemplates(false);
  }

  const handleSelectOrderTemplate = useCallback(async (orderTemplate: OrderTemplate) => {
    setSelectedOrderTemplate(orderTemplate);
    window.history.replaceState(null, '', `/#${location.pathname.replace(pkgid || '', orderTemplate.id.toString())}`);
  }, []);

  const handleAddCustomerContact = useCallback(() => {
    if (isAdmin) {
        onAddCustomerContact(Number(brid) || 0);
    }
    else {
        onAddCustomerContact(Number(brid) || 0, session?.user?.id);
    }
  }, [brid]);

  const handleCancelNote = async () => {
    setNotes(notes.slice(0, -1));
  };

  const handleSaveNote = useCallback(async (noteContent: string) => {
    const lastNote = notes[notes.length - 1];
    lastNote.content = noteContent;
    lastNote.created = new Date().toISOString();
    setNotes([...notes.slice(0, -1), lastNote]);
  }, [notes]);

  return (
    <>
      <OrderCreate
        activeStep={ORDER_STEP.BOOKING}
        extraText={extraText(selectedOrderTemplate?.id)}
        isAdmin={isAdmin}
        selectedOrder={selectedOrderTemplate}
        onCreateOrder={handleSaveOrder}
      >
        { errors.length > 0 && errors.map((error, index) =>
        <Alert key={index} type="error">{error}</Alert>) }
        <Booking
            {...contactsProps}
            {...fields}
            isAdmin={isAdmin}
            notes={notes}
            description={selectedOrderTemplate?.description || ''}
            onAddCustomerContact={handleAddCustomerContact}
            onAddNote={onAddNote} 
            onCancelNote={handleCancelNote}
            onSaveNote={handleSaveNote}
            onAskToChangeWorker={handleChangeWorker}
            onChangePackage={handleChangePackage}
            preferredWorker={selectedPreferredWorker}
            selectedOrderTemplate={selectedOrderTemplate}
            services={selectedOrderTemplate?.services || []}
            owner={`${session.user?.firstName} ${session.user?.lastName}`}
            ref={bookingRef}
            />
      </OrderCreate>
      {showModal && (
        <FindPersonModal
          onFindPerson={handleFindPerson}
          onSelectPerson={handleSelect}
          persons={
            workers.map((item) => ({
            email: item.email,
            id: item.id,
            name: item.name,
          }))
          }
          onClose={onCloseModal}
        />
        )}
      {contactsProps.showContactModal && (
        <ContactModal
          contact={contactsProps.contactToEdit}
          onAddNew={contactsProps.onAddContact}
          onEdit={contactsProps.onEditContact}
          onClose={contactsProps.onCloseContactModal}
          type={contactsProps.modalType}
          />
        )}
        {contactsProps.showRemoveModal && (
            <RemoveModal
                description="Are you sure you want to remove this contact?"
                onCancel={contactsProps.onCloseRemoveModal}
                onClose={contactsProps.onCloseRemoveModal}
                onRemove={contactsProps.onRemoveContact}
                title="Remove Contact"
            />
        )}
      {showConfirmModal && (
        <ConfirmModal
          description="Your order has now been sent to our amazing bookings team and we will process your booking A.S.A.P."
          onClose={handleConfirmClosed}
          onConfirm={handleConfirmClosed}
          title="Order Sent!"
        />
      )}
      {showChangePackageModal && (
        <ChangePackageModal
          isAdmin={isAdmin}
          loading={loadingOrderTemplates}
          onClose={onCloseChangePackageModal}
          onSelectOrderTemplate={handleSelectOrderTemplate}
          orderTemplates={orderTemplateApi.orderTemplates}
        />) }
    </>
  );
}

export default OrderNewView;
